import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Button } from "./ui/button";
import { Trash2, Plus, Pencil, Upload, Download, FileSpreadsheet, Wand2, X, Check, Loader2 } from "lucide-react";

/**
 * Petit gestionnaire de plats côté ADMIN : liste + ajout + suppression.
 * Sert à peupler une vitrine restaurant créée manuellement depuis l'admin.
 * Utilise les endpoints /restaurateur/admin/{restaurantId}/menu.
 */
export default function AdminMenuManager({ restaurantId, restaurantName, initialPublished = false, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price_eur: "", category: "", photo_data_url: "" });
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState(initialPublished);
  const [publishing, setPublishing] = useState(false);
  const [bulkPhotoBusy, setBulkPhotoBusy] = useState(false);

  const generateMissingPhotos = async () => {
    const missing = items.filter((i) => !i.photo_url).length;
    if (missing === 0) { toast.info("Tous les plats ont déjà une photo."); return; }
    const ok = window.confirm(`Générer ${missing} photo(s) IA (Gemini) pour les plats sans image ?\n\nCela peut prendre 1-2 minutes.`);
    if (!ok) return;
    setBulkPhotoBusy(true);
    try {
      const { data } = await api.post(`/restaurateur/admin/${restaurantId}/menu/generate-missing-photos`, {}, { timeout: 300000 });
      toast.success(`✅ ${data.generated} photo(s) générée(s)${data.failed ? ` · ${data.failed} échec(s)` : ""}`);
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkPhotoBusy(false); }
  };

  // Édition des infos de base du restaurant (nom, adresse, photo, etc.)
  const [tab, setTab] = useState("dishes"); // dishes | info
  const [infoForm, setInfoForm] = useState({
    name: "", address: "", description: "", cuisine: "", phone: "", city: "", postal_code: "", logo_url: "", cover_url: "", delivery_radius_km: 0, opening_hours_text: "",
  });
  const [infoSaving, setInfoSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [dishesRes, restoRes] = await Promise.all([
        api.get(`/restaurateur/admin/${restaurantId}/menu`),
        api.get(`/restaurateur/admin/restaurant/${restaurantId}`),
      ]);
      setItems(dishesRes.data.items || []);
      const r = restoRes.data || {};
      setInfoForm({
        name: r.name || "", address: r.address || "", description: r.description || "",
        cuisine: r.cuisine || "", phone: r.phone || "", city: r.city || "Bruxelles",
        postal_code: r.postal_code || "", logo_url: r.logo_url || "", cover_url: r.cover_url || "",
        delivery_radius_km: Number(r.delivery_radius_km) || 0,
        opening_hours_text: r.opening_hours_text || "",
      });
      setPublished(!!r.published);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [restaurantId]);

  const saveInfo = async () => {
    if (!infoForm.name.trim() || !infoForm.address.trim()) {
      toast.error("Nom et adresse sont obligatoires.");
      return;
    }
    if (infoForm.logo_url === "loading") {
      toast.error("Photo en cours de lecture, patiente 1 seconde…");
      return;
    }
    setInfoSaving(true);
    try {
      const payload = { ...infoForm };
      // Si logo_url commence par "data:", on l'envoie comme photo_data_url pour upload storage.
      if ((infoForm.logo_url || "").startsWith("data:")) {
        payload.photo_data_url = infoForm.logo_url;
        delete payload.logo_url;
      }
      await api.patch(`/restaurateur/admin/restaurant/${restaurantId}`, payload);
      toast.success("Informations mises à jour ✓");
      // Recharge pour récupérer l'URL servie par le backend
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setInfoSaving(false); }
  };

  const onLogoFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setInfoForm((f) => ({ ...f, logo_url: reader.result }));
    reader.onerror = () => toast.error("Lecture de la photo échouée");
    setInfoForm((f) => ({ ...f, logo_url: "loading" }));
    reader.readAsDataURL(file);
  };

  const publish = async () => {
    if (items.length === 0) {
      if (!window.confirm("Aucun plat n'est encore ajouté. Publier quand même ?")) return;
    }
    setPublishing(true);
    try {
      await api.post(`/restaurateur/admin/${restaurantId}/publish`);
      setPublished(true);
      toast.success(`🎉 « ${restaurantName} » est en ligne sur noldgo.com !`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPublishing(false); }
  };

  const unpublish = async () => {
    if (!window.confirm("Retirer le restaurant de noldgo.com ? Il ne sera plus visible du public (repasse en brouillon).")) return;
    setPublishing(true);
    try {
      await api.post(`/restaurateur/admin/${restaurantId}/unpublish`);
      setPublished(false);
      toast.info("Restaurant retiré (brouillon)");
    } catch (e) { toast.error(apiError(e)); }
    finally { setPublishing(false); }
  };

  const onFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo_data_url: reader.result }));
    reader.onerror = () => toast.error("Lecture de la photo échouée");
    setForm((f) => ({ ...f, photo_data_url: "loading" }));
    reader.readAsDataURL(file);
  };

  const addDish = async () => {
    if (!form.name.trim() || !form.price_eur) {
      toast.error("Nom et prix requis.");
      return;
    }
    if (form.photo_data_url === "loading") {
      toast.error("Photo en cours de lecture, patiente 1 seconde…");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        price_eur: parseFloat(form.price_eur),
        category: form.category.trim() || null,
        photo_data_url: (form.photo_data_url || "").startsWith("data:") ? form.photo_data_url : null,
        active: true,
        position: items.length,
      };
      await api.post(`/restaurateur/admin/${restaurantId}/menu`, payload);
      toast.success("Plat ajouté ✓");
      setForm({ name: "", description: "", price_eur: "", category: "", photo_data_url: "" });
      // Reset explicite du file input pour permettre la re-sélection immédiate
      const fi = document.querySelector('[data-testid="admin-dish-photo"]');
      if (fi) fi.value = "";
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const deleteDish = async (itemId) => {
    if (!window.confirm("Supprimer ce plat ?")) return;
    try {
      await api.delete(`/restaurateur/admin/${restaurantId}/menu/${itemId}`);
      setItems((s) => s.filter((x) => x.id !== itemId));
      toast.success("Supprimé");
    } catch (e) { toast.error(apiError(e)); }
  };

  // Import CSV / Photo IA
  const [importMode, setImportMode] = useState(null); // null | "csv" | "photo"

  // Édition inline d'un plat existant
  const [editing, setEditing] = useState(null); // objet plat en cours d'édition
  const [editForm, setEditForm] = useState({ name: "", description: "", price_eur: "", category: "", photo_data_url: "" });
  const [editAiBusy, setEditAiBusy] = useState(false); // génération IA photo en cours
  const [editSaving, setEditSaving] = useState(false);
  const startEdit = (it) => {
    setEditing(it);
    setEditForm({
      name: it.name || "",
      description: it.description || "",
      price_eur: String(it.price_eur ?? ""),
      category: it.category || "",
      photo_data_url: "",
    });
  };
  const onEditFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setEditForm((f) => ({ ...f, photo_data_url: reader.result }));
    reader.onerror = () => toast.error("Lecture de la photo échouée");
    setEditForm((f) => ({ ...f, photo_data_url: "loading" }));
    reader.readAsDataURL(file);
  };
  const saveEdit = async () => {
    if (!editing) return;
    if (!editForm.name.trim() || !editForm.price_eur) {
      toast.error("Nom et prix requis.");
      return;
    }
    if (editForm.photo_data_url === "loading") {
      toast.error("Photo en cours de lecture, patiente 1 seconde…");
      return;
    }
    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        description: editForm.description.trim(),
        price_eur: parseFloat(editForm.price_eur),
        category: editForm.category.trim() || null,
        photo_data_url: (editForm.photo_data_url || "").startsWith("data:") ? editForm.photo_data_url : null,
        active: editing.active !== false,
        position: editing.position || 0,
      };
      await api.patch(`/restaurateur/admin/${restaurantId}/menu/${editing.id}`, payload);
      toast.success("Plat mis à jour ✓");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setEditSaving(false); }
  };

  return (
    <div data-testid="admin-menu-manager" className="space-y-4">
      {/* Statut + actions */}
      <div className="flex items-center justify-between gap-3 rounded-xl bg-[#FDF8F0] border border-[#F0EBE1] p-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-[#75807A]">Statut :</span>
          {published ? (
            <span data-testid="rt-status-live" className="inline-flex items-center gap-1.5 rounded-full bg-[#16a34a]/15 text-[#16a34a] px-3 py-1 text-xs font-black uppercase tracking-wide">
              <span className="w-2 h-2 rounded-full bg-[#16a34a] animate-pulse" /> En ligne
            </span>
          ) : (
            <span data-testid="rt-status-draft" className="inline-flex items-center gap-1.5 rounded-full bg-[#F59E0B]/15 text-[#B45309] px-3 py-1 text-xs font-black uppercase tracking-wide">
              📝 Brouillon
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {onClose && (
            <Button data-testid="rt-save-close" onClick={onClose} className="h-9 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-xs font-semibold px-3">
              💾 Sauvegarder & fermer
            </Button>
          )}
          {published ? (
            <Button data-testid="rt-unpublish" onClick={unpublish} disabled={publishing} className="h-9 rounded-lg bg-white border-2 border-[#D32F2F] text-[#D32F2F] hover:bg-[#D32F2F]/5 text-xs font-bold px-3 disabled:opacity-60">
              Retirer de la ligne
            </Button>
          ) : (
            <Button data-testid="rt-publish" onClick={publish} disabled={publishing} className="h-9 rounded-lg bg-[#16a34a] hover:bg-[#128a3e] text-white text-xs font-bold px-4 disabled:opacity-60">
              🚀 {publishing ? "Publication…" : "Publier sur noldgo.com"}
            </Button>
          )}
        </div>
      </div>

      <div className="border-t border-[#F0EBE1] pt-4">
        {/* Tabs Infos / Plats */}
        <div className="flex items-center gap-2 mb-4">
          <button data-testid="rt-tab-info" onClick={() => setTab("info")} className={`h-9 rounded-lg px-4 text-sm font-bold transition-all ${tab === "info" ? "bg-[#F26419] text-white" : "bg-white border-2 border-[#E2DFD8] text-[#75807A]"}`}>
            📝 Infos du resto
          </button>
          <button data-testid="rt-tab-dishes" onClick={() => setTab("dishes")} className={`h-9 rounded-lg px-4 text-sm font-bold transition-all ${tab === "dishes" ? "bg-[#F26419] text-white" : "bg-white border-2 border-[#E2DFD8] text-[#75807A]"}`}>
            🍽️ Plats ({items.length})
          </button>
        </div>

        {tab === "info" && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-[#75807A] uppercase">Nom *</label>
              <input data-testid="rt-info-name" value={infoForm.name} onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })} className="mt-1 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#75807A] uppercase">Adresse *</label>
              <input data-testid="rt-info-address" value={infoForm.address} onChange={(e) => setInfoForm({ ...infoForm, address: e.target.value })} className="mt-1 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Cuisine</label>
                <input data-testid="rt-info-cuisine" value={infoForm.cuisine} onChange={(e) => setInfoForm({ ...infoForm, cuisine: e.target.value })} className="mt-1 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Téléphone</label>
                <input data-testid="rt-info-phone" value={infoForm.phone} onChange={(e) => setInfoForm({ ...infoForm, phone: e.target.value })} className="mt-1 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Ville</label>
                <input data-testid="rt-info-city" value={infoForm.city} onChange={(e) => setInfoForm({ ...infoForm, city: e.target.value })} className="mt-1 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Code postal</label>
                <input data-testid="rt-info-postal" value={infoForm.postal_code} onChange={(e) => setInfoForm({ ...infoForm, postal_code: e.target.value })} className="mt-1 w-full h-11 rounded-xl border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#75807A] uppercase">Description</label>
              <textarea data-testid="rt-info-description" value={infoForm.description} onChange={(e) => setInfoForm({ ...infoForm, description: e.target.value })} rows={3} className="mt-1 w-full rounded-xl border-2 border-[#E2DFD8] px-3 py-2 text-sm resize-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#75807A] uppercase">Horaires d'ouverture</label>
              <textarea data-testid="rt-info-hours" value={infoForm.opening_hours_text} onChange={(e) => setInfoForm({ ...infoForm, opening_hours_text: e.target.value })} rows={2} maxLength={400}
                placeholder="Ex: Lun-Ven 11h-22h · Sam-Dim 12h-23h · Fermé le mardi"
                className="mt-1 w-full rounded-xl border-2 border-[#E2DFD8] px-3 py-2 text-sm resize-none" />
              <div className="text-[11px] text-[#75807A] mt-1">Affiché aux clients dans "Infos restaurant".</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#75807A] uppercase">Photo / logo</label>
              <div className="mt-1 flex items-center gap-3">
                <input data-testid="rt-info-logo-file" type="file" accept="image/*" onChange={(e) => onLogoFile(e.target.files?.[0])} className="text-sm flex-1" />
                {infoForm.logo_url && <img src={infoForm.logo_url} alt="logo" className="w-14 h-14 rounded-xl object-cover border border-[#E2DFD8]" />}
              </div>
            </div>
            <div className="rounded-xl bg-[#FFF3E6] border border-[#F26419]/30 p-3">
              <label className="text-xs font-semibold text-[#7A3A00] uppercase flex items-center gap-1.5">
                🚴 Rayon de livraison
              </label>
              <div className="mt-1 flex items-center gap-3">
                <input
                  data-testid="rt-info-radius"
                  type="number" min="0" max="30" step="0.5"
                  value={infoForm.delivery_radius_km || ""}
                  placeholder="0 = pas de limite"
                  onChange={(e) => setInfoForm({ ...infoForm, delivery_radius_km: Math.max(0, Math.min(30, parseFloat(e.target.value) || 0)) })}
                  className="w-32 h-10 rounded-lg border-2 border-[#F26419]/40 px-3 text-sm font-bold"
                />
                <span className="text-sm text-[#7A3A00]">km max autour du restaurant</span>
              </div>
              <p className="text-xs text-[#7A3A00] mt-1.5 opacity-80">
                Les clients hors zone verront une erreur au checkout. Laissez à <b>0</b> pour désactiver cette limite.
              </p>
            </div>
            <Button data-testid="rt-info-save" onClick={saveInfo} disabled={infoSaving} className="w-full h-11 rounded-lg bg-[#F26419] hover:bg-[#D94F2B] text-white text-sm font-bold disabled:opacity-60">
              💾 {infoSaving ? "Enregistrement…" : "Enregistrer les modifications"}
            </Button>
          </div>
        )}

        {tab === "dishes" && (
        <>
        <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
          <h4 className="font-head font-bold text-base">🍽️ Plats de « {restaurantName} »</h4>
          <div className="flex items-center gap-2 flex-wrap">
            <button data-testid="admin-menu-generate-missing-photos" onClick={generateMissingPhotos} disabled={bulkPhotoBusy}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border-2 border-[#059669] text-[#059669] hover:bg-[#F0FDF4] text-xs font-bold disabled:opacity-60">
              {bulkPhotoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {bulkPhotoBusy ? "Génération…" : "🎨 Photos manquantes (IA)"}
            </button>
            <button data-testid="admin-menu-import-photo" onClick={() => setImportMode("photo")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border-2 border-[#7C3AED] text-[#7C3AED] hover:bg-[#F5F0FF] text-xs font-bold">
              <Wand2 className="w-4 h-4" /> Import IA (photo)
            </button>
            <button data-testid="admin-menu-import-csv" onClick={() => setImportMode("csv")}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] text-xs font-bold">
              <FileSpreadsheet className="w-4 h-4" /> Importer des plats (CSV)
            </button>
          </div>
        </div>

        {/* Liste des plats */}
        {loading ? (
          <div className="text-sm text-[#75807A] py-2">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="text-sm text-[#75807A] py-2 italic">Aucun plat pour l'instant.</div>
        ) : (
          <div className="space-y-2 mb-4 max-h-64 overflow-y-auto pr-1">
            {items.map((it) => (
              <div key={it.id} data-testid={`admin-dish-${it.id}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-[#FDF8F0] border border-[#F0EBE1]">
                {it.photo_url && <img src={it.photo_url} alt={it.name} className="w-11 h-11 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{it.name}</div>
                  <div className="text-xs text-[#75807A] truncate">{it.category || "—"} · {it.description || ""}</div>
                </div>
                <div className="text-sm font-bold text-[#F26419] whitespace-nowrap">{Number(it.price_eur).toFixed(2)} €</div>
                <button data-testid={`admin-dish-edit-${it.id}`} onClick={() => startEdit(it)} className="p-1.5 rounded-lg hover:bg-orange-50 text-[#F26419]">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid={`admin-dish-delete-${it.id}`} onClick={() => deleteDish(it.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Formulaire d'ajout */}
        <div className="rounded-xl border-2 border-dashed border-[#E2DFD8] p-3 space-y-2 bg-white">
          <div className="text-xs font-bold text-[#75807A] uppercase tracking-wide">Ajouter un plat</div>
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="admin-dish-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom du plat" className="h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm col-span-2" />
            <input data-testid="admin-dish-price" type="number" step="0.01" value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })} placeholder="Prix €" className="h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
            <input data-testid="admin-dish-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Catégorie (Pizza, Boisson…)" className="h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
            <textarea data-testid="admin-dish-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description" className="rounded-lg border-2 border-[#E2DFD8] px-3 py-2 text-sm col-span-2 resize-none" />
            <div className="col-span-2 flex items-center gap-2">
              <input data-testid="admin-dish-photo" type="file" accept="image/*" onChange={(e) => onFile(e.target.files?.[0])} className="text-xs" />
              {form.photo_data_url === "loading" && <span className="text-xs text-[#75807A] italic">📷 Lecture…</span>}
              {form.photo_data_url && form.photo_data_url !== "loading" && <img src={form.photo_data_url} alt="preview" className="w-10 h-10 rounded object-cover border border-[#E2DFD8]" />}
            </div>
          </div>
          <Button data-testid="admin-dish-add" onClick={addDish} disabled={saving} className="w-full h-10 rounded-lg bg-gradient-to-r from-[#F26419] to-[#D94F2B] hover:opacity-90 text-white text-sm font-bold disabled:opacity-60">
            <Plus className="w-4 h-4 mr-1" /> {saving ? "Ajout…" : "Ajouter le plat"}
          </Button>
        </div>
        </>
        )}
      </div>

      {editing && (
        <div data-testid="admin-dish-edit-modal" className="fixed inset-0 z-[3500] bg-black/50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setEditing(null)}>          <div className="bg-white rounded-2xl p-6 max-w-md w-full space-y-3 my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-head font-black text-lg">✏️ Modifier le plat</h3>
              <button onClick={() => setEditing(null)} className="text-2xl text-[#75807A] hover:text-[#1A1A1A]">×</button>
            </div>
            <div className="space-y-2">
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Nom</label>
                <input data-testid="admin-dish-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1 w-full h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Prix (€)</label>
                <input data-testid="admin-dish-edit-price" type="number" step="0.01" value={editForm.price_eur} onChange={(e) => setEditForm({ ...editForm, price_eur: e.target.value })} className="mt-1 w-full h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Catégorie</label>
                <input data-testid="admin-dish-edit-category" value={editForm.category} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} className="mt-1 w-full h-10 rounded-lg border-2 border-[#E2DFD8] px-3 text-sm" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Description</label>
                <textarea data-testid="admin-dish-edit-description" value={editForm.description} onChange={(e) => setEditForm({ ...editForm, description: e.target.value })} rows={3} className="mt-1 w-full rounded-lg border-2 border-[#E2DFD8] px-3 py-2 text-sm resize-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#75807A] uppercase">Photo (remplace l'actuelle)</label>
                <div className="mt-1 flex items-center gap-2">
                  <input data-testid="admin-dish-edit-photo" type="file" accept="image/*" onChange={(e) => onEditFile(e.target.files?.[0])} className="text-xs flex-1" />
                  <button type="button" data-testid="admin-dish-edit-ai-photo"
                    onClick={async () => {
                      const n = (editForm.name || "").trim();
                      if (n.length < 2) { toast.error("Renseignez d'abord le nom du plat."); return; }
                      setEditAiBusy(true);
                      setEditForm((f) => ({ ...f, photo_data_url: "loading" }));
                      try {
                        const { data } = await api.post("/restaurateur/admin/generate-item-photo", { name: n, description: (editForm.description || "").trim() });
                        if (data?.data_url) {
                          setEditForm((f) => ({ ...f, photo_data_url: data.data_url }));
                          toast.success("Photo générée par l'IA ✨");
                        } else {
                          setEditForm((f) => ({ ...f, photo_data_url: "" }));
                          toast.error("Aucune image renvoyée par l'IA.");
                        }
                      } catch (e) {
                        setEditForm((f) => ({ ...f, photo_data_url: "" }));
                        toast.error(e?.response?.data?.detail || "Génération IA échouée.");
                      } finally { setEditAiBusy(false); }
                    }}
                    disabled={editAiBusy || !editForm.name?.trim()}
                    className="inline-flex items-center gap-1 h-8 px-3 rounded-lg bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] hover:opacity-90 text-white text-xs font-bold disabled:opacity-50 shrink-0">
                    {editAiBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                    {editAiBusy ? "Génération…" : "Générer avec IA"}
                  </button>
                  {editForm.photo_data_url === "loading" && <span className="text-xs text-[#75807A] italic">📷 Lecture…</span>}
                  {editForm.photo_data_url && editForm.photo_data_url !== "loading" && <img src={editForm.photo_data_url} alt="preview" className="w-10 h-10 rounded object-cover border border-[#E2DFD8]" />}
                  {!editForm.photo_data_url && editing.photo_url && <img src={editing.photo_url} alt="current" className="w-10 h-10 rounded object-cover border border-[#E2DFD8] opacity-60" />}
                </div>
                <div className="text-[10px] text-[#75807A] mt-1">Laisse vide pour garder la photo actuelle. L'IA utilise le nom + description du plat.</div>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button data-testid="admin-dish-edit-cancel" onClick={() => setEditing(null)} className="flex-1 h-10 rounded-lg bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold">Annuler</Button>
              <Button data-testid="admin-dish-edit-save" onClick={saveEdit} disabled={editSaving} className="flex-1 h-10 rounded-lg bg-[#F26419] hover:bg-[#D94F2B] text-white text-sm font-bold disabled:opacity-60">
                {editSaving ? "Enregistrement…" : "💾 Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      )}
      {importMode && (
        <AdminImportDialog
          restaurantId={restaurantId}
          mode={importMode}
          onClose={() => setImportMode(null)}
          onImported={() => { setImportMode(null); load(); }}
        />
      )}
    </div>
  );
}

function AdminImportDialog({ restaurantId, mode, onClose, onImported }) {
  const [csv, setCsv] = useState("");
  const [replace, setReplace] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [skipMarkup, setSkipMarkup] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewMeta, setPreviewMeta] = useState({ markupApplied: true, ratePct: 25 });

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) { toast.error("Photo trop lourde (max 8 Mo)."); return; }
    const r = new FileReader();
    r.onload = () => { setPhotoDataUrl(r.result); setPhotoPreview(r.result); setPreview(null); };
    r.readAsDataURL(f);
  };

  const onCsvFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Fichier CSV trop lourd (max 2 Mo)."); e.target.value = ""; return; }
    const r = new FileReader();
    r.onload = () => { setCsv(String(r.result || "")); toast.success(`Fichier « ${f.name} » chargé — vérifiez puis cliquez sur Importer.`); };
    r.onerror = () => toast.error("Impossible de lire le fichier.");
    r.readAsText(f, "utf-8");
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/restaurateur/admin/${restaurantId}/menu/import-csv/template`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "nold-go-menu-modele.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Téléchargement impossible."); }
  };

  const doCsvImport = async () => {
    if (!csv.trim()) { toast.error("CSV vide. Collez du contenu ou chargez un fichier."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurateur/admin/${restaurantId}/menu/import-csv`, { csv, replace });
      toast.success(`✓ ${data.imported} plats importés (${data.skipped} lignes ignorées)`);
      onImported();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doPhotoAnalyze = async () => {
    if (!photoDataUrl) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurateur/admin/${restaurantId}/menu/import-photo`, { photo_data_url: photoDataUrl, auto_create: false, skip_markup: skipMarkup });
      setPreview(data.items || []);
      setPreviewMeta({ markupApplied: !!data.markup_applied, ratePct: Number(data.commission_rate_pct || 25) });
      toast.success(`${data.count || 0} plats détectés · ${data.markup_applied ? `marge Nold Go +${data.commission_rate_pct}% appliquée` : "prix Uber/Deliveroo conservés"}`);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const doPhotoConfirm = async () => {
    if (!preview || preview.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/restaurateur/admin/${restaurantId}/menu/import-photo`, { photo_data_url: photoDataUrl, auto_create: true, skip_markup: skipMarkup });
      toast.success(`✓ ${data.inserted || 0} plats importés depuis la photo`);
      onImported();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[3600] bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto" onClick={onClose} data-testid="admin-import-dialog">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-3xl rounded-t-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-black text-xl">
              {mode === "csv" ? <><FileSpreadsheet className="inline w-5 h-5 mr-2" />Importer des plats (CSV)</> : <><Wand2 className="inline w-5 h-5 mr-2" />Import par photo (IA)</>}
            </h3>
            <button onClick={onClose} className="w-9 h-9 rounded-full hover:bg-[#F0EBE1] grid place-items-center" data-testid="admin-import-close"><X className="w-5 h-5" /></button>
          </div>

          {mode === "csv" ? (
            <div className="space-y-4">
              <div className="rounded-xl bg-[#FAF5EC] p-3 text-xs text-[#3B423E]">
                <b>Colonnes attendues :</b> <code>nom, prix, description, catégorie</code> (catégorie optionnelle).<br />
                Séparateur : <code>,</code> ou <code>;</code>. Prix en euros (point ou virgule).<br />
                <code className="text-[11px]">Pizza Margherita,12.50,Tomate mozzarella basilic,Pizzas</code>
              </div>
              <div className="flex gap-2 flex-wrap">
                <button onClick={downloadTemplate} data-testid="admin-import-csv-template"
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border-2 border-[#F26419] text-[#F26419] hover:bg-[#FFF3E6] font-bold text-xs">
                  <Download className="w-4 h-4" /> Télécharger le modèle CSV
                </button>
                <label className="inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-white border border-[#E2DFD8] hover:border-[#F26419] text-[#1A1A1A] font-bold text-xs cursor-pointer">
                  <Upload className="w-4 h-4" /> Charger un fichier .csv
                  <input type="file" accept=".csv,text/csv" onChange={onCsvFile} className="hidden" data-testid="admin-import-csv-file" />
                </label>
              </div>
              <textarea value={csv} onChange={(e) => setCsv(e.target.value)} rows={10} data-testid="admin-import-csv-input"
                placeholder="Collez ici votre CSV ou chargez un fichier ci-dessus…"
                className="w-full px-3 py-2 rounded-xl border border-[#E2DFD8] focus:border-[#F26419] outline-none font-mono text-sm" />
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="admin-import-csv-replace" />
                Remplacer les plats existants (⚠️ supprime la carte actuelle)
              </label>
              <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">Annuler</button>
                <button onClick={doCsvImport} disabled={busy} data-testid="admin-import-csv-submit"
                  className="flex-1 h-12 rounded-full bg-[#F26419] hover:bg-[#D94D0E] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Importer
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-xl bg-[#FAF5EC] p-3 text-xs text-[#3B423E]">
                <b>Prenez en photo la carte papier</b> — l'IA (Gemini) va extraire les plats et prix automatiquement. Vous validez ensuite avant insertion.
              </div>
              {!preview ? (
                <>
                  <label className="block border-2 border-dashed border-[#E2DFD8] rounded-xl p-8 text-center cursor-pointer hover:border-[#7C3AED] transition-colors" data-testid="admin-import-photo-drop">
                    {photoPreview ? (
                      <img src={photoPreview} alt="" className="max-h-64 mx-auto rounded-lg" />
                    ) : (
                      <div>
                        <Wand2 className="w-10 h-10 mx-auto text-[#7C3AED] opacity-70" />
                        <div className="mt-2 text-sm font-semibold text-[#1A1A1A]">Choisir la photo de la carte</div>
                        <div className="text-xs text-[#75807A]">JPG/PNG · max 8 Mo</div>
                      </div>
                    )}
                    <input type="file" accept="image/*" onChange={onPhoto} className="hidden" />
                  </label>
                  <label className="flex items-start gap-2 text-xs text-[#3B423E] cursor-pointer bg-[#FFF3E6] rounded-xl p-3 border border-[#F26419]/30" data-testid="admin-import-photo-skip-markup-label">
                    <input
                      type="checkbox"
                      checked={skipMarkup}
                      onChange={(e) => setSkipMarkup(e.target.checked)}
                      data-testid="admin-import-photo-skip-markup"
                      className="mt-0.5"
                    />
                    <span>
                      <b>Prix déjà majorés</b> (screenshot Uber Eats / Deliveroo)<br />
                      <span className="text-[#75807A]">Cochez si l'image vient d'une plateforme qui applique déjà sa marge, pour ne pas doubler la marge Nold Go.</span>
                    </span>
                  </label>
                  <div className="flex gap-3">
                    <button onClick={onClose} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">Annuler</button>
                    <button onClick={doPhotoAnalyze} disabled={!photoDataUrl || busy} data-testid="admin-import-photo-analyze"
                      className="flex-1 h-12 rounded-full bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
                      {busy ? "Analyse en cours…" : "Analyser la carte"}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-semibold text-[#1A1A1A]">Plats détectés ({preview.length})</div>
                  {previewMeta.markupApplied ? (
                    <div className="rounded-xl bg-[#F0FDF4] border border-[#059669]/30 px-3 py-2 text-xs text-[#065F46]" data-testid="admin-import-photo-markup-banner">
                      ✓ Marge Nold Go <b>+{previewMeta.ratePct}%</b> appliquée automatiquement · <span className="text-[#3B423E]">prix original → prix client</span>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-[#FFF3E6] border border-[#F26419]/30 px-3 py-2 text-xs text-[#7C2D12]" data-testid="admin-import-photo-markup-banner">
                      ⚠️ Aucune marge ajoutée (Uber Eats / Deliveroo)
                    </div>
                  )}
                  <ul className="max-h-64 overflow-y-auto rounded-xl border border-[#E2DFD8] divide-y divide-[#E2DFD8]" data-testid="admin-import-photo-preview">
                    {preview.map((p, i) => (
                      <li key={i} className="p-3 flex justify-between items-start gap-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-[#1A1A1A] truncate">{p.name}</div>
                          {p.description && <div className="text-xs text-[#75807A] truncate">{p.description}</div>}
                        </div>
                        <div className="shrink-0 text-right">
                          {previewMeta.markupApplied && p.price_original_eur != null && Number(p.price_original_eur) !== Number(p.price_eur) && (
                            <div className="text-[11px] text-[#75807A] line-through">{Number(p.price_original_eur).toFixed(2)} €</div>
                          )}
                          <div className="font-head font-black text-[#F26419]">{Number(p.price_eur).toFixed(2)} €</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                  <div className="flex gap-3">
                    <button onClick={() => setPreview(null)} className="flex-1 h-12 rounded-full bg-[#F0EBE1] hover:bg-[#E2DFD8] font-bold">Reprendre</button>
                    <button onClick={doPhotoConfirm} disabled={busy} data-testid="admin-import-photo-confirm"
                      className="flex-1 h-12 rounded-full bg-[#16a34a] hover:bg-[#128a3e] text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-60">
                      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      Confirmer l'import
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}